import { useParams } from 'react-router-dom';
import { useAuthUser } from '../contexts/AuthUserContext';
import { useCustomer } from '../contexts/CustomerContext';
import NotFoundPage from '../pages/NotFoundPage';
import { Ripples } from 'ldrs/react'; // Using the same loader for consistency

/**
 * A guard component that validates if the current user is authorized to access
 * the customer ID specified in the URL.
 */
const CustomerRouteValidator = ({ children }) => {
  const { customerId: urlCustomerId } = useParams();
  const user = useAuthUser();
  const { currentCustomer, isImpersonating, isLoading: isCustomerContextLoading } = useCustomer();

  // Display a loading indicator while the user and customer contexts are initializing.
  // This prevents a premature redirect to the 404 page.
  // We check for user.email as a sign that the AuthUserContext is fully loaded.
  if (isCustomerContextLoading || !user?.email) {
    return (
      <div className="flex items-center justify-center h-full">
        <Ripples size="150" speed="2.5" color="#00aeef" />
      </div>
    );
  }

  let isAuthorized = false;

  if (user.isHeliosAdmin) {
    // A Helios Admin is authorized if:
    // 1. They are accessing the default 'helios' customer (even when not impersonating), OR
    // 2. They are actively impersonating and the URL matches the impersonated customer
    isAuthorized = urlCustomerId === 'helios' || (isImpersonating && urlCustomerId === currentCustomer?.id);
  } else {
    // A non-admin (client user) is only authorized if the customerId in the URL
    // matches their own assigned customerId from their profile.
    isAuthorized = urlCustomerId === user.customerId;
  }

  // If the user is not authorized for the requested customerId, show the 404 page.
  if (!isAuthorized) {
    return <NotFoundPage />;
  }

  // If all authorization checks pass, render the intended child routes (CustomerRoutes).
  return children;
};

export default CustomerRouteValidator;